import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaStar,
  FaStarHalfAlt,
  FaQuoteLeft,
  FaUsers,
  FaTruck,
  FaClock,
  FaUserCircle,
} from 'react-icons/fa';
import { fetchWebReviewStats, fetchWebTestimonials } from '../../api/webPublicApi';
import './Reviews.css';

const FALLBACK_TESTIMONIALS = [
  {
    name: 'Customer 1',
    location: 'Miyapur, Hyderabad',
    rating: 5,
    text: 'Good taste and fresh delivery. The meals are satisfying and usually arrive on time.',
  },
  {
    name: 'Customer 2',
    location: 'Kukatpally, Hyderabad',
    rating: 5,
    text: 'Meal plans are convenient and portions are decent. Helpful for daily lunch and dinner.',
  },
  {
    name: 'Customer 3',
    location: 'Hitech City, Hyderabad',
    rating: 4,
    text: 'Food quality is good and packaging is clean. Sometimes delivery can be a little delayed.',
  },
  {
    name: 'Customer 4',
    location: 'Nizampet, Hyderabad',
    rating: 5,
    text: 'Tasty home-style food with good menu variety. Customer support was responsive.',
  },
  {
    name: 'Customer 5',
    location: 'Bachupally, Hyderabad',
    rating: 4,
    text: 'Good quality meals for regular orders. Value for money compared to outside food.',
  },
  {
    name: 'Customer 6',
    location: 'Madinaguda, Hyderabad',
    rating: 4,
    text: 'Liked the flavour and hygiene. Would be great if delivery was a bit faster during peak hours.',
  },
  {
    name: 'Customer 7',
    location: 'Miyapur, Hyderabad',
    rating: 3,
    text: 'Food was okay overall. Some dishes were good while some felt average for my taste.',
  },
  {
    name: 'Customer 8',
    location: 'Nallagandla, Hyderabad',
    rating: 2,
    text: 'Delivery was late and one item was not hot enough. Hoping for better consistency.',
  },
  {
    name: 'Customer 9',
    location: 'KPHB, Hyderabad',
    rating: 1,
    text: 'Order experience was not good on that day. Needs improvement in support and delivery coordination.',
  },
];

const FALLBACK_STATS = [
  { icon_key: 'star', value: '4.9/5', label: 'Google Rating' },
  { icon_key: 'users', value: '1,200+', label: 'Total Reviews' },
  { icon_key: 'clock', value: '98%', label: 'On-time Delivery' },
  { icon_key: 'truck', value: '6,500+', label: 'Orders Delivered' },
];

const STAT_ICONS = {
  star: <FaStar />,
  users: <FaUsers />,
  clock: <FaClock />,
  truck: <FaTruck />,
};

function mapTestimonial(row) {
  return {
    id: row.id,
    name: row.name || 'Customer',
    location: row.location || '',
    rating: Number(row.rating) || 0,
    text: row.text || '',
    relativeTime: row.relativeTime || row.relative_time || '',
  };
}

function mapStat(row) {
  return {
    id: row.id,
    label: row.label || '',
    value: row.value || '',
    icon_key: STAT_ICONS[row.icon_key] ? row.icon_key : 'star',
  };
}

const Reviews = () => {
  const [reviews, setReviews] = useState(FALLBACK_TESTIMONIALS);
  const [stats, setStats] = useState(FALLBACK_STATS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [testimonialRows, statRows] = await Promise.all([
          fetchWebTestimonials(),
          fetchWebReviewStats(),
        ]);
        if (cancelled) return;
        if (testimonialRows.length) setReviews(testimonialRows.map(mapTestimonial));
        if (statRows.length) setStats(statRows.map(mapStat));
      } catch {
        /* keep designed fallbacks */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayStats = useMemo(
    () => stats.map((item, index) => ({
      key: item.id || `${item.label}-${index}`,
      icon: STAT_ICONS[item.icon_key] || <FaStar />,
      value: item.value,
      label: item.label,
    })),
    [stats],
  );

  return (
    <div className="reviews-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="container">
            <h1>CUSTOMER REVIEWS</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Reviews</span>
            </div>
          </div>
        </div>
      </div>

      <section className="reviews-hero">
        <div className="container reviews-hero__grid">
          <div className="reviews-hero__content">
            <span className="section-tag">What our customers say</span>
            <h2 className="section-title">
              Delicious food, trusted delivery, and people who keep coming back
            </h2>
            <p className="reviews-hero__desc">
              From healthy meal plans to tasty daily specials, our customers love the consistent
              quality and quick service. Discover why Digirasa is one of Hyderabad&apos;s most
              recommended cloud kitchens.
            </p>
            <Link to="/our-menu" className="btn-primary">
              Explore our menu
            </Link>
          </div>
          <div className="reviews-hero__stats">
            {displayStats.map((item) => (
              <div key={item.key} className="reviews-stat-card">
                <div className="reviews-stat-card__icon">{item.icon}</div>
                <div>
                  <h3>{item.value}</h3>
                  <p>{item.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="reviews-testimonials">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Testimonials</span>
            <h2 className="section-title">Reviews from happy customers</h2>
          </div>
          <div className="reviews-grid">
            {reviews.map((review, index) => (
              <div key={review.id || `${review.name}-${index}`} className="review-card">
                <div className="review-card__quote">
                  <FaQuoteLeft />
                </div>
                <div className="review-card__rating">
                  {Array.from({ length: Math.floor(review.rating) }).map((_, starIndex) => (
                    <FaStar key={starIndex} />
                  ))}
                  {review.rating % 1 !== 0 && <FaStarHalfAlt />}
                </div>
                <p>{review.text}</p>
                <div className="review-card__author">
                  <div className="review-card__author-avatar" aria-hidden="true">
                    <FaUserCircle />
                  </div>
                  <div className="review-card__author-meta">
                    <h4>{review.name}</h4>
                    <span>
                      {review.location}
                      {review.relativeTime ? ` • ${review.relativeTime}` : ''}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="reviews-trust">
        <div className="container">
          <div className="reviews-trust__card">
            <div>
              <span className="section-tag">Trusted, every day</span>
              <h2 className="section-title">Trusted by hundreds across Hyderabad</h2>
              <p>
                Our customers choose Digirasa for reliable delivery, fresh ingredients, and taste
                that feels like home. Every review helps us improve and serve you better.
              </p>
            </div>
            <div className="reviews-trust__actions">
              <Link to="/contact" className="btn-secondary">
                Contact Support
              </Link>
              <Link to="/meal-plans" className="btn-primary">
                View meal plans
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
